<template>
	<div>
		<v-dialog v-model="dialogOpen" max-width="760" persistent scrollable>
			<v-card class="financeiro-dialog" outlined>
				<v-card-title class="financeiro-dialog__title">
					<span>Contas bancárias</span>
					<v-spacer />
					<v-btn icon :disabled="saving || deleting" @click="fecharDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider />

				<v-card-text class="pt-6 pb-0">
					<div class="financeiro-info-box mb-6">
						Cadastre e acompanhe as contas bancárias usadas nos lançamentos financeiros. Essa tela existe apenas para facilitar o vínculo dos lançamentos a uma conta específica.
					</div>

					<div class="financeiro-contas__header mb-4">
						<div>
							<div class="financeiro-contas__title">Contas cadastradas</div>
							<div class="financeiro-contas__subtitle">Use nomes simples para localizar a conta no lançamento.</div>
						</div>
						<v-btn class="financeiro-save-btn" depressed :disabled="saving || deleting" @click="abrirFormularioCadastro">
							<v-icon left size="18">mdi-plus</v-icon>
							Cadastrar Conta Bancária
						</v-btn>
					</div>

					<v-expand-transition>
						<v-card v-show="formOpen" class="financeiro-conta-form mb-5" outlined>
							<v-card-text class="pt-5 pb-2">
								<div class="financeiro-conta-form__title mb-4">
									{{ isEditMode ? 'Editar conta bancária' : 'Nova conta bancária' }}
								</div>
								<v-form ref="contaForm" v-model="formValid" lazy-validation>
									<v-row dense>
										<v-col cols="12" md="6">
											<v-text-field
												v-model.trim="form.nome"
												label="Nome da conta"
												outlined
												dense
												:rules="[rules.required]"
											/>
										</v-col>

										<v-col cols="12" md="6">
											<v-text-field
												v-model.trim="form.banco"
												label="Banco"
												outlined
												dense
											/>
										</v-col>

										<v-col cols="12" md="4">
											<v-text-field
												v-model.trim="form.agencia"
												label="Agência"
												outlined
												dense
											/>
										</v-col>

										<v-col cols="12" md="4">
											<v-text-field
												v-model.trim="form.conta"
												label="Conta"
												outlined
												dense
											/>
										</v-col>

										<v-col cols="12" md="4">
											<v-text-field
												v-model.trim="form.observacoes"
												label="Observação"
												outlined
												dense
											/>
										</v-col>
									</v-row>
								</v-form>
							</v-card-text>
							<v-card-actions class="px-4 pb-4 pt-0">
								<v-spacer />
								<v-btn text :disabled="saving || deleting" @click="cancelarFormulario">Cancelar</v-btn>
								<v-btn class="financeiro-save-btn" depressed :loading="saving" :disabled="saving || deleting" @click="salvarConta">
									{{ isEditMode ? 'Salvar alterações' : 'Salvar conta' }}
								</v-btn>
							</v-card-actions>
						</v-card>
					</v-expand-transition>

					<div v-if="contasOrdenadas.length" class="financeiro-contas-lista">
						<v-card
							v-for="conta in contasOrdenadas"
							:key="conta.id"
							class="financeiro-conta-item mb-3"
							outlined
						>
							<v-card-text class="py-4">
								<div class="financeiro-conta-item__row">
									<div class="financeiro-conta-item__content">
										<div class="financeiro-conta-item__nome">{{ conta.nome }}</div>
										<div class="financeiro-conta-item__meta">{{ formatContaResumo(conta) }}</div>
										<div v-if="conta.observacoes" class="financeiro-conta-item__obs mt-2">
											{{ conta.observacoes }}
										</div>
									</div>
									<div class="financeiro-conta-item__actions">
										<v-btn icon small :disabled="saving || deleting" @click="editarConta(conta)">
											<v-icon size="18" color="#12b57a">mdi-pencil-outline</v-icon>
										</v-btn>
										<v-btn icon small :disabled="saving || deleting" @click="abrirExclusao(conta)">
											<v-icon size="18" color="#ef4444">mdi-delete-outline</v-icon>
										</v-btn>
									</div>
								</div>
							</v-card-text>
						</v-card>
					</div>

					<div v-else class="financeiro-empty-state">
						Nenhuma conta bancária cadastrada até o momento.
					</div>
				</v-card-text>

				<v-card-actions class="px-6 pb-6 pt-4">
					<v-spacer />
					<v-btn text :disabled="saving || deleting" @click="fecharDialog">Fechar</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
			<v-card class="financeiro-dialog" outlined>
				<v-card-title class="financeiro-dialog__title">
					<span>Excluir conta bancária</span>
					<v-spacer />
					<v-btn icon :disabled="deleting" @click="deleteDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<div class="financeiro-confirmacao__texto">
						Tem certeza que deseja excluir <strong>{{ contaExclusao.nome || '-' }}</strong>?
					</div>
					<div class="financeiro-confirmacao__aviso">
						Os lançamentos que estiverem vinculados a essa conta ficarão sem conta bancária associada.
					</div>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="deleting" @click="deleteDialogOpen = false">Cancelar</v-btn>
					<v-btn class="financeiro-delete-btn" depressed :loading="deleting" :disabled="deleting" @click="confirmarExclusao">
						Excluir
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import financeiroServices from './financeiroServices';

const createInitialForm = () => ({
	id: null,
	nome: '',
	banco: '',
	agencia: '',
	conta: '',
	observacoes: '',
});

export default {
	name: 'ContasFinanceiroDialog',

	props: {
		value: {
			type: Boolean,
			default: false,
		},
		contas: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			dialogOpen: false,
			formOpen: false,
			formValid: false,
			saving: false,
			deleting: false,
			deleteDialogOpen: false,
			contaExclusao: {},
			form: createInitialForm(),
			rules: {
				required: (v) => Boolean(v) || 'Campo obrigatorio',
			},
		};
	},

	computed: {
		isEditMode() {
			return Boolean(this.form.id);
		},

		contasOrdenadas() {
			return Array.isArray(this.contas)
				? [...this.contas].sort((a, b) => String(a.nome || '').localeCompare(String(b.nome || '')))
				: [];
		},
	},

	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.dialogOpen = newValue;
			},
		},

		dialogOpen(newValue) {
			this.$emit('input', newValue);
			if (!newValue && !this.saving) {
				this.cancelarFormulario();
			}
		},
	},

	methods: {
		formatContaResumo(conta) {
			const partes = [conta.banco, conta.agencia ? `Ag. ${conta.agencia}` : null, conta.conta ? `Conta ${conta.conta}` : null]
				.filter(Boolean);

			return partes.length ? partes.join(' • ') : 'Conta disponível para vínculo nos lançamentos';
		},

		abrirFormularioCadastro() {
			this.form = createInitialForm();
			this.formOpen = true;
			this.formValid = false;
			if (this.$refs.contaForm) {
				this.$refs.contaForm.resetValidation();
			}
		},

		editarConta(conta) {
			this.form = {
				id: conta.id,
				nome: conta.nome || '',
				banco: conta.banco || '',
				agencia: conta.agencia || '',
				conta: conta.conta || '',
				observacoes: conta.observacoes || '',
			};
			this.formOpen = true;
			this.formValid = false;
			if (this.$refs.contaForm) {
				this.$refs.contaForm.resetValidation();
			}
		},

		abrirExclusao(conta) {
			this.contaExclusao = conta || {};
			this.deleteDialogOpen = true;
		},

		cancelarFormulario() {
			this.formOpen = false;
			this.form = createInitialForm();
			this.formValid = false;
			if (this.$refs.contaForm) {
				this.$refs.contaForm.resetValidation();
			}
		},

		fecharDialog() {
			if (!this.saving && !this.deleting) {
				this.dialogOpen = false;
			}
		},

		async salvarConta() {
			if (!this.$refs.contaForm?.validate()) {
				return;
			}

			this.saving = true;

			try {
				const payload = {
					nome: this.form.nome,
					banco: this.form.banco || null,
					agencia: this.form.agencia || null,
					conta: this.form.conta || null,
					observacoes: this.form.observacoes || null,
				};

				const response = this.isEditMode
					? await financeiroServices.atualizarConta(this.form.id, payload)
					: await financeiroServices.criarConta(payload);

				this.$emit('saved', response.data);
				this.cancelarFormulario();

				if (this.$toasted) {
					this.$toasted.show(this.isEditMode ? 'Conta bancária atualizada com sucesso' : 'Conta bancária cadastrada com sucesso');
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel salvar a conta bancaria';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			} finally {
				this.saving = false;
			}
		},

		async confirmarExclusao() {
			if (!this.contaExclusao?.id) {
				return;
			}

			this.deleting = true;

			try {
				await financeiroServices.excluirConta(this.contaExclusao.id);
				this.deleteDialogOpen = false;
				this.$emit('saved', { deletedId: this.contaExclusao.id });
				this.contaExclusao = {};

				if (this.$toasted) {
					this.$toasted.show('Conta bancária excluída com sucesso');
				}
			} catch (error) {
				const errorMessage = error?.response?.data?.error || 'Nao foi possivel excluir a conta bancaria';
				if (this.$toasted) {
					this.$toasted.error(errorMessage);
				}
			} finally {
				this.deleting = false;
			}
		},
	},
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.financeiro-dialog {
	border-radius: 24px;
	font-family: "Inter", sans-serif;
	border: 1px solid #e6ebf1 !important;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12) !important;
}

.financeiro-dialog__title {
	padding: 24px 24px 18px;
	font-size: 20px;
	font-weight: 700;
	color: #1f2a44;
}

.financeiro-info-box {
	border: 1px solid #dbe7f3;
	border-radius: 16px;
	background: #f8fbff;
	color: #516174;
	font-size: 14px;
	line-height: 1.6;
	padding: 16px 18px;
}

.financeiro-contas__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
}

.financeiro-contas__title {
	font-size: 15px;
	font-weight: 800;
	color: #1f2a44;
}

.financeiro-contas__subtitle {
	font-size: 13px;
	color: #7a8ca6;
	margin-top: 4px;
}

.financeiro-conta-form,
.financeiro-conta-item {
	border-radius: 16px !important;
	border: 1px solid #e6ebf1 !important;
	box-shadow: none !important;
}

.financeiro-conta-form {
	background: #fbfdff !important;
}

.financeiro-conta-form__title {
	font-size: 15px;
	font-weight: 800;
	color: #1f2a44;
}

.financeiro-conta-item__row {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
}

.financeiro-conta-item__content {
	min-width: 0;
	flex: 1;
}

.financeiro-conta-item__actions {
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
}

.financeiro-conta-item__nome {
	font-size: 15px;
	font-weight: 800;
	color: #1f2a44;
}

.financeiro-conta-item__meta {
	font-size: 13px;
	color: #64748b;
	margin-top: 4px;
}

.financeiro-conta-item__obs {
	font-size: 13px;
	color: #506076;
	line-height: 1.5;
}

.financeiro-empty-state {
	border: 1px dashed #cbd5e1;
	border-radius: 16px;
	padding: 20px;
	font-size: 14px;
	color: #64748b;
	text-align: center;
	background: #fff;
}

.financeiro-save-btn {
	background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 12px;
	text-transform: none;
	padding: 0 18px !important;
	box-shadow: 0 12px 28px rgba(18, 181, 122, 0.22) !important;
}

.financeiro-delete-btn {
	background: linear-gradient(135deg, #f87171 0%, #ef4444 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 12px;
	text-transform: none;
}

.financeiro-confirmacao__texto {
	font-size: 15px;
	line-height: 1.6;
	color: #334155;
}

.financeiro-confirmacao__aviso {
	margin-top: 10px;
	font-size: 13px;
	color: #7b8aa2;
}
</style>
